import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ChartEvent,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toNumber } from 'lodash';
import { useRef, useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';

import { useRatesQuery } from '@/api/query';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppStore } from '@/stores/useAppStore';
import { formatYieldString, verboseTerm } from '@/utils/helper-functions';

const style = getComputedStyle(document.body);
const tsColorBorder = style.getPropertyValue('--ts-color-border');
const tsColorDescription = style.getPropertyValue('--ts-color-description');
const tsColorText = style.getPropertyValue('--ts-color-text');
const tsColorSurface = style.getPropertyValue('--ts-color-surface');
const tsColorAccent = style.getPropertyValue('--ts-color-accent');
const tsFontCircular = style.getPropertyValue('--ts-font-circular');

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartDataLabels,
  Title,
  Tooltip,
  Legend
);

export default function RatesChart() {
  const {
    selectedCurrency,
    selectedRating,
    selectedSector,
    setSelectedProduct,
  } = useAppStore();
  const { rates: rawRates } = useRatesQuery().data || {};
  const { width } = useWindowSize();

  const data = useMemo(() => {
    if (!rawRates) return null;
    return rawRates[selectedCurrency][selectedRating][selectedSector];
  }, [rawRates, selectedCurrency, selectedRating, selectedSector]);

  const isDataProvided = useMemo(() => data !== null, [data]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartRef = useRef<any>(null);
  const maxRate = useMemo(
    () => (data ? Math.max(...Object.values(data).map(toNumber)) : 0),
    [data]
  );

  const terms = useMemo(() => {
    if (!data) return [];
    const allTerms = Object.keys(data);
    if (allTerms.includes('1d') && !data['1d']) {
      return allTerms.filter((term) => term !== '1d');
    }
    return allTerms;
  }, [data]);

  const rates = useMemo(
    () =>
      terms.map((term) => {
        if (isDataProvided) {
          return data?.[term] || 0;
        }
        return 0;
      }),
    [data, isDataProvided, terms]
  );

  const handleClick = useCallback(
    (event: ChartEvent) => {
      const chart = chartRef.current;
      const points = chart.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: true },
        true
      );
      if (points.length) {
        const firstPoint = points[0];
        const label = chart.data.labels[firstPoint.index];
        const value =
          chart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];
        setSelectedProduct({
          term: label,
          rate: toNumber(value),
        });
      }
    },
    [setSelectedProduct]
  );

  const labelFontSize = useMemo(() => {
    if (width >= 1440) return 16;
    if (width >= 768) return 14;
    if (width >= 512) return 11;
    return 10;
  }, [width]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: handleClick,
      onHover: (event: ChartEvent, chartElement) => {
        const target = event.native?.target as HTMLElement | null;
        if (target) {
          target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        }
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: tsColorDescription,
            font: {
              family: tsFontCircular,
              size: width < 1084 ? 10 : 12,
              weight: '300',
            },
            callback: (value) => {
              const components = terms[toNumber(value)].split(' ');
              if (components[0] === '1d') {
                return 'Next day';
              }
              if (components.length === 1) {
                return verboseTerm(components[0]);
              }
              return [
                verboseTerm(components.slice(0, -1).join(' ')),
                `(${components.slice(-1)})`,
              ];
            },
          },
        },
        y: {
          suggestedMax: maxRate + 0.5,
          display: false,
          grid: { display: false },
          border: { display: false },
        },
      },
      plugins: {
        datalabels: {
          color: tsColorText,
          anchor: 'end',
          align: 'end',
          formatter: (value: number) => {
            if (value === 0) {
              return '';
            }
            return `${formatYieldString(value)}%`;
          },
          offset: 2,
          font: {
            family: tsFontCircular,
            weight: 700,
            size: labelFontSize,
          },
        },
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: tsColorSurface,
          displayColors: false,
          callbacks: {
            label(context) {
              return `${formatYieldString(context.parsed.y)}%`;
            },
            title(context) {
              const components = context[0].label.split(' ');
              if (components[0] === '1d') {
                return 'Next day';
              }
              if (components.length === 1) {
                return verboseTerm(components[0]);
              }
              return [
                verboseTerm(components.slice(0, -1).join(' ')),
                `(${components.slice(-1)})`,
              ].join(' ');
            },
          },
          titleFont: {
            family: tsFontCircular,
            size: 14,
            weight: '300',
          },
          titleColor: tsColorDescription,
          padding: 12,
          bodyFont: {
            family: tsFontCircular,
            size: 16,
            weight: '500',
          },
          titleMarginBottom: 8,
          bodySpacing: 4,
          bodyColor: tsColorText,
          borderColor: tsColorBorder,
          borderWidth: 1,
          boxPadding: 4,
        },
      },
    }),
    [handleClick, labelFontSize, maxRate, terms, width]
  );

  const chartData: ChartData<'bar'> = useMemo(
    () => ({
      labels: terms,
      datasets: [
        {
          data: rates,
          backgroundColor: tsColorAccent,
          borderRadius: 5,
          borderSkipped: false,
          barPercentage: width < 1084 ? 1.1 : 1,
        },
      ],
    }),
    [rates, terms, width]
  );

  return (
    <Bar
      ref={chartRef}
      options={options}
      data={chartData}
      className="h-350px max-h-[350px]"
    />
  );
}
